import { ChangeDetectorRef, Component, Inject, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ButtonTriggerEvent } from 'src/app/common/components/button/button.component';
import { ErrorAlertService } from 'src/app/common/services/error.alert/error.alert.service';
import { ImageService } from 'src/app/common/services/image/image.service';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import { clone, isEmptyArray } from 'src/app/common/utils/object.extensions';
import { config } from 'src/app/config/config';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import {
  ConfirmModalActions,
  ConfirmModalComponent,
  ConfirmModalData,
} from 'src/app/modules/shared/components/confirm.modal/confirm.modal.component';

import { PlaceAdditionalData, VisitorPlace } from 'src/app/modules/shared/models/visitor.place';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { MatMenuTrigger } from '@angular/material/menu';
import { BaseComponent } from 'src/app/common/components/base.component';
import { ActivatedRoute } from '@angular/router';

@Component({
  templateUrl: './edit.place.images.modal.component.html',
  styleUrls: ['./edit.place.images.modal.component.scss'],
})
export class EditPlaceImagesModalComponent extends BaseComponent {
  place: VisitorPlace;
  actions: EditPlaceImagesModalActions;
  placeAdditionalData: PlaceAdditionalData;
  isGoogleInfoLoaded = false;
  isDragging = false;
  dragFrom = '';
  dropTargets = [];
  imageLink: string = '';

  coverImage: string = '';
  publishedImages: string[] = [];
  compensateForPublished: any[] = [];

  availableImages: string[] = [];
  compensateForAvailable: any[] = [];

  ownedImages: {
    url: string;
    status: 'initial' | 'uploading' | 'uploaded' | 'failed' | 'done';
  }[] = [];
  compensateForOwned: any[] = [];

  googleKey = '';
  totalImageCount = 10;
  totalImageCountThatSysAdminSet = 5;
  useFirstImgAsCover = true;
  selectedImageFiles: File[];

  errorAlertService = inject(ErrorAlertService);
  imageService = inject(ImageService);
  visitorPlaceService = inject(VisitorPlaceService);
  modalService = inject(ModalService);
  cdr = inject(ChangeDetectorRef);

  constructor(
    private activatedRoute: ActivatedRoute,
    private dialogRef: MatDialogRef<EditPlaceImagesModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    data: IEditPlaceImagesModalData
  ) {
    super({ activatedRoute });
    this.place = Object.assign({}, data.place);
    this.actions = Object.assign({}, data.actions);
   
    this.totalImageCountThatSysAdminSet =
      this.frontendSettings?.customSettings?.placeImagesShownCount ||
      this.totalImageCountThatSysAdminSet;

    this.getPlaceAdditionalData();
  }

  public async getPlaceAdditionalData() {
    if (this.place.socialName === 'google') {
      if (
        isEmptyArray(this.place.allowedGooglePhotos) ||
        this.place.allowedGooglePhotos.any((x) => !x.includes('amazonaws')) ||
        (!isEmptyArray(this.place.blockedGooglePhotos) &&
          this.place.blockedGooglePhotos.any((x) => !x.includes('amazonaws')))
      ) {
        return await this.updateImages(false);
      }
    }

    this.buildImages();
    this.compensate();
    this.isGoogleInfoLoaded = true;
  }

  public async confirm(event: ButtonTriggerEvent) {
    if (!this.publishedImages.length) {
      event.immediateComplete();
      this.errorAlertService.showErrorAlert('Please select at least one allowed image');
      return;
    }
    this.publishedImages = this.publishedImages.map((x) => x['url'] || x);
    if (!this.place.cover) this.place.cover = { source: '', isDefault: false };
    // now use the first image from publishedImages as the cover image, as the cover image is now put in the publishedImages initially
    this.coverImage = this.publishedImages[0];
    // for client uploaded cover image, it will be re-added in buildImages() when opening this popup
    if (this.isClientUploaded(this.publishedImages[0])) {
      this.publishedImages.shift();
    }

    this.place.allowedGooglePhotos = this.publishedImages.map((x) => x);
    this.place.blockedGooglePhotos = this.availableImages.map((x) => x['url'] || x);

    await this.uploadOwnedImagesToAWS();

    if (this.ownedImages && this.ownedImages.any((img) => img.status === 'failed')) {
      event.immediateComplete();
      return;
    }

    this.place.ownedPhotos = this.ownedImages.map((x) => x.url);
    // to let users see the last image uploaded successfully
    this.ownedImages.any((x) => x.status === 'uploaded') && (await this.myWait(1000));
    event.immediateComplete();

    this.actions.confirm(event, this.place, this.coverImage);
    this.dialogRef.close();
  }

  public async uploadOwnedImagesToAWS() {
    for (const img of this.ownedImages) {
      if (img.url.includes('amazonaws')) {
        img.status = 'done';
        continue;
      }
      img.status = 'uploading';
      this.imageService
        .uploadBase64ImageToAWS(img.url, config.compressImgWidth, 'places/iti-user-upload/')
        .subscribe((res) => {
          if (res.success) {
            img.url = res.url;
            img.status = 'uploaded';
            setTimeout(() => {
              img.status = 'done';
            }, 3000);
          } else {
            img.status = 'failed';
          }
        });
    }
  }

  public removeOwnImage(index: number) {
    this.ownedImages.splice(index, 1);
  }

  public getImage(imgUrl: string | { url: string }, isRealReference = true) {
    if (!imgUrl) return '';

    const url = imgUrl['url'] || imgUrl;

    if (url.startsWith('http') || url.startsWith('data:')) {
      return url;
    }

    let url1 = this.visitorPlaceService.getGooglePlaceImageFullUrl(
      url,
      this.googleKey,
      isRealReference
    );
    return this.imageService.imageSourceUrl(url1);
  }

  public removeOwnedImageFromAllowed(image: string | { url: string, status: string }, removeFrom: 'published' | 'available' | 'owned') {
    if (removeFrom === 'published') {
      this.publishedImages = this.publishedImages.filter((x) => x !== image);
    } else if (removeFrom === 'available') {
      this.availableImages = this.availableImages.filter((x) => x !== image);
    } else if (removeFrom === 'owned') {
      this.ownedImages = this.ownedImages.filter((x) => x !== image);
    }
    this.compensate();
  }

  public isClientUploaded(image: string | { url: string, status: string }) {
    return typeof image === 'string' ? image.includes('iti-user-upload') : (image as { url: string, status: string }).url.includes('iti-user-upload');
  }

  public isWaitingForUpload(image: string) {
    if (!image) return false;
    return !image.includes('amazonaws');
  }

  public close(): void {
    if (this.actions.cancel) {
      this.actions.cancel();
    }

    this.dialogRef.close();
  }

  public async updateImages(isAskForConfirm = true) {
    const run = async () => {
      this.isGoogleInfoLoaded = false;
      this.visitorPlaceService
        .getGooglePlaceDetails(this.place.hubName, this.place.id, true)
        .subscribe((googlePlaceAdditionalData) => {
          this.placeAdditionalData = googlePlaceAdditionalData as any;

          if (!this.placeAdditionalData) {
            this.errorAlertService.showErrorAlert('This Google place has no photos');
            this.close();
          }
          if (this.placeAdditionalData.allowedGooglePhotos) {
            let allPhotos = this.placeAdditionalData.allowedGooglePhotos;
            this.googleKey = this.placeAdditionalData.appKey;

            let realReferences = allPhotos.map((x) =>
              this.visitorPlaceService.getGooglePlaceReference(x)
            );
            this.place.allowedGooglePhotos = realReferences;
            this.place.blockedGooglePhotos = isAskForConfirm
              ? []
              : this.placeAdditionalData.blockedGooglePhotos;
          }

          if (this.place.cover && this.place.cover.source) {
            this.place.cover.source = this.imageService.imageSource(this.place);
          }

          this.buildImages();
          this.compensate();
          this.isGoogleInfoLoaded = true;
        });
    };

    if (isAskForConfirm) {
      let actions = <ConfirmModalActions>{
        confirm: async (event: ButtonTriggerEvent) => {
          event.complete();
          await run();
        },
      };
      let data = <ConfirmModalData>{
        confirmModalIcon: 'warning',
        confirmModalTitle: 'Alert',
        confirmModalDescription:
          'Are you sure you want to update Google images of this place, this will refresh all Google images and reset them to allowed status?',
        confirmModalConfirmButtonText: 'Yes',
        confirmModalCancelButtonText: 'Cancel',
        isAsync: true,
      };

      this.modalService.show({
        component: ConfirmModalComponent,
        providers: [
          { provide: ConfirmModalActions, useValue: actions },
          { provide: ConfirmModalData, useValue: data },
        ],
      });
    } else {
      await run();
    }
  }

  public async enterImageLink(event: ButtonTriggerEvent, inputLinkMenuTrigger: MatMenuTrigger) {
    if (!this.imageLink) return;
    if (this.ownedImages.length >= 10) {
      this.errorAlertService.showErrorAlert(`You can't upload more than 10 images`);
      return;
    }

    this.ownedImages.push({
      url: this.imageLink,
      status: 'initial',
    });
    await this.uploadOwnedImagesToAWS();
    this.imageLink = '';
    inputLinkMenuTrigger?.closeMenu();

    // let actions = <InputModalActions>{
    //   confirm: async (event: ButtonTriggerEvent, url) => {
    //     console.log(url);
    //     this.ownedImages.push({
    //       url,
    //       status: 'initial',
    //     });
    //     await this.uploadOwnedImagesToAWS();
    //   },
    // };

    // this.modalService.show({
    //   component: InputModalComponent,
    //   data: {
    //     actions: actions,
    //     data: new InputModalData('Enter image link', 'Link', null, 'OK', 'CANCEL', false),
    //   },
    //   panelClass: ['ig-edit-modal', '!w-[1000px]'],
    // });
  }

  public async onImageChange($event) {
    if (
      $event.srcElement.files.length > 10 ||
      $event.srcElement.files.length + this.ownedImages.length > 10
    ) {
      this.errorAlertService.showErrorAlert(`You can't upload more than 10 images`);
    }

    this.selectedImageFiles = $event.srcElement.files;

    if (this.selectedImageFiles && this.selectedImageFiles.length > 0) {
      for (let i = 0; i < this.selectedImageFiles.length; i++) {
        if (this.ownedImages.length >= 10) break;

        const selectedImageFiles = await this.imageService.compressImageFileToBase64(
          this.selectedImageFiles[i]
        );
        this.ownedImages.push({
          url: selectedImageFiles,
          status: 'initial',
        });
      }

      await this.uploadOwnedImagesToAWS();
    }
    // reset file input
    $event.srcElement.value = '';
  }
  private buildImages() {
    this.publishedImages = [];
    this.availableImages = [];
    this.ownedImages = [];
    let hasUserUploadedCoverImage = false;
    let allowedPhotos = clone(this.place.allowedGooglePhotos) || [];

    if (this.place.cover?.source) {
      // insert the client uploaded cover image as the first one of the allowed images
      // replace the first one with the cover image if the first one was not client uploaded
      // so that the cover image will always show as the first one in allowed images, and there is no duplicated image issue
      if (this.isClientUploaded(this.place.cover.source)) {
        this.publishedImages.push(this.place.cover.source);
        hasUserUploadedCoverImage = true;
      } else {
        allowedPhotos[0] = this.place.cover.source;
      }
    }

    if (this.place.allowedGooglePhotos && this.place.allowedGooglePhotos.length >= 0) {
      let images = [...this.publishedImages, ...allowedPhotos];
      this.publishedImages = images.slice(0, this.totalImageCountThatSysAdminSet);
      this.availableImages = allowedPhotos
        .slice(
          hasUserUploadedCoverImage
            ? this.totalImageCountThatSysAdminSet - 1
            : this.totalImageCountThatSysAdminSet
        )
        .concat(this.place.blockedGooglePhotos);
    }

    if (this.place.ownedPhotos && this.place.ownedPhotos.length >= 0) {
      this.ownedImages = this.place.ownedPhotos.map((x) => {
        return { url: x, status: 'initial' as any };
      });
    }
  }

  // allowed row and blocked row both show 10 images in a flex div, so here to compensate to make them as 10 if dragged out one
  private compensate() {
    if (this.publishedImages && this.publishedImages.length >= 0) {
      this.compensateForPublished = [];
      for (let i = 0; i < this.totalImageCountThatSysAdminSet - this.publishedImages.length; i++) {
        this.compensateForPublished.push({});
      }
    }

    if (this.availableImages && this.availableImages.length >= 0) {
      this.compensateForAvailable = [];
      for (let i = 0; i < this.totalImageCount - this.availableImages.length; i++) {
        this.compensateForAvailable.push({});
      }
    }
  }

  private myWait(milSeconds) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(null);
      }, milSeconds);
    });
  }

  // Add this at the top of your component class
  dragOverIndex: number = -1;
  draggedItemIndex: number = -1;
  dragOverList: 'published' | 'available' | 'owned' | null = null;
  currentDragList: 'published' | 'available' | 'owned' | null = null;

  onDragStart(event: DragEvent, index: number, list: 'published' | 'available' | 'owned') {
    // Make sure we're not trying to drag a compensate element
    const target = event.target as HTMLElement;
    if (target.classList.contains('compensate-item') || target.closest('.compensate-item')) {
      event.preventDefault();
      return;
    }

    this.draggedItemIndex = index;
    this.currentDragList = list;
    event.dataTransfer?.setData('text/plain', index.toString());
    event.dataTransfer!.effectAllowed = 'move';
    target.classList.add('dragging');
  }

  onDragOver(event: DragEvent, index: number, list: 'published' | 'available' | 'owned') {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
    this.dragOverList = list;

    // Allow drops between published and available lists
    if (this.currentDragList && this.currentDragList !== list) {
      if (
        (this.currentDragList === 'published' && list === 'available') ||
        (this.currentDragList === 'available' && list === 'published')
      ) {
        event.dataTransfer!.dropEffect = 'move';
      } else {
        event.dataTransfer!.dropEffect = 'none';
        return;
      }
    }
  }

  onDragEnter(event: DragEvent, index: number, list: 'published' | 'available' | 'owned') {
    event.preventDefault();

    // Only update the drag over state if we're in the same list or between allowed lists
    if (
      this.currentDragList === list ||
      (this.currentDragList === 'published' && list === 'available') ||
      (this.currentDragList === 'available' && list === 'published')
    ) {
      this.dragOverIndex = index;
      this.dragOverList = list;
    }
  }

  onDragLeave(event: DragEvent) {
    // Only reset if we're not dragging over another valid target
    const target = event.target as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement;

    if (!target.contains(relatedTarget)) {
      this.dragOverIndex = -1;
    }
  }

  onDrop1(event: DragEvent, dropIndex: number, targetList: 'published' | 'available' | 'owned') {
    event.preventDefault();
    event.stopPropagation();

    const isDroppingOnCompensate =
      (event.target as HTMLElement).classList.contains('compensate-item') ||
      (event.target as HTMLElement).closest('.compensate-item');

    if (this.currentDragList === targetList) {
      // If dropping on a compensate element in the same list, move to the end
      if (isDroppingOnCompensate) {
        if (targetList === 'published') {
          this.moveToEndOfList('published');
        } else if (targetList === 'available') {
          this.moveToEndOfList('available');
        }
      } else {
        // Normal reordering within the same list
        this.reorderWithinList(targetList, dropIndex);
      }
    } else if (this.currentDragList && this.currentDragList !== targetList) {
      // Handle moving between different lists
      if (isDroppingOnCompensate) {
        // If dropping on a compensate element, append to the end of the target list
        if (targetList === 'published') {
          this.moveBetweenLists(
            this.currentDragList,
            'published',
            this.publishedImages.length,
            false
          );
        } else if (targetList === 'available') {
          this.moveBetweenLists(
            this.currentDragList,
            'available',
            this.availableImages.length,
            false
          );
        }
      } else {
        // If dropping on an existing item, replace it
        this.moveBetweenLists(this.currentDragList, targetList, dropIndex, true);
      }
    }

    this.updateUI();
  }

  private moveToEndOfList(list: 'published' | 'available') {
    if (list === 'published' && this.currentDragList === 'published') {
      const itemToMove = this.publishedImages[this.draggedItemIndex];
      this.publishedImages.splice(this.draggedItemIndex, 1);
      this.publishedImages.push(itemToMove);
    } else if (list === 'available' && this.currentDragList === 'available') {
      const itemToMove = this.availableImages[this.draggedItemIndex];
      this.availableImages.splice(this.draggedItemIndex, 1);
      this.availableImages.push(itemToMove);
    }
  }

  private reorderWithinList(list: 'published' | 'available' | 'owned', dropIndex: number) {
    let temp: any;
    if (list === 'published') {
      temp = this.publishedImages[this.draggedItemIndex];
      this.publishedImages[this.draggedItemIndex] = this.publishedImages[dropIndex];
      this.publishedImages[dropIndex] = temp;
    } else if (list === 'available') {
      temp = this.availableImages[this.draggedItemIndex];
      this.availableImages[this.draggedItemIndex] = this.availableImages[dropIndex];
      this.availableImages[dropIndex] = temp;
    } else if (list === 'owned') {
      temp = this.ownedImages[this.draggedItemIndex];
      this.ownedImages[this.draggedItemIndex] = this.ownedImages[dropIndex];
      this.ownedImages[dropIndex] = temp;
    }
  }

  private moveBetweenLists(
    sourceList: 'published' | 'available' | 'owned',
    targetList: 'published' | 'available' | 'owned',
    dropIndex: number,
    replace: boolean = false
  ) {
    // Get the item being moved
    let itemToMove: any;
    let removedItem: any = null;

    // Get the item from the source list
    if (sourceList === 'published') {
      if (this.publishedImages.length <= 1) return;
      itemToMove = this.publishedImages[this.draggedItemIndex];
      this.publishedImages.splice(this.draggedItemIndex, 1);
    } else if (sourceList === 'available') {
      itemToMove = this.availableImages[this.draggedItemIndex];
      this.availableImages.splice(this.draggedItemIndex, 1);
    } else if (sourceList === 'owned') {
      itemToMove = this.ownedImages[this.draggedItemIndex];
      if (targetList !== 'owned') {
        this.ownedImages.splice(this.draggedItemIndex, 1);
      }
    } else {
      return;
    }

    // Handle the target list
    if (targetList === 'published') {
      // Check if we're at the limit
      if (this.publishedImages.length >= this.totalImageCountThatSysAdminSet && !replace) {
        // If we removed an item from published, put it back
        if (sourceList === 'published') {
          this.publishedImages.splice(this.draggedItemIndex, 0, itemToMove);
        }
        return;
      }

      if (replace && dropIndex < this.publishedImages.length) {
        // Save the item being replaced
        removedItem = this.publishedImages[dropIndex];
        // Replace the item
        this.publishedImages[dropIndex] = itemToMove;
      } else {
        // Insert the item
        this.publishedImages.splice(dropIndex, 0, itemToMove);
      }
    } else if (targetList === 'available') {
      if (replace && dropIndex < this.availableImages.length) {
        // Save the item being replaced
        removedItem = this.availableImages[dropIndex];
        // Replace the item
        this.availableImages[dropIndex] = itemToMove;
      } else {
        // Insert the item
        this.availableImages.splice(dropIndex, 0, itemToMove);
      }
    } else if (targetList === 'owned') {
      if (replace && dropIndex < this.ownedImages.length) {
        removedItem = this.ownedImages[dropIndex];
        this.ownedImages[dropIndex] = itemToMove;
      } else {
        this.ownedImages.splice(dropIndex, 0, itemToMove);
      }
    }

    // If we replaced an item, add it back to the source list
    if (removedItem) {
      if (sourceList === 'published') {
        this.publishedImages.splice(this.draggedItemIndex, 0, removedItem);
      } else if (sourceList === 'available') {
        this.availableImages.splice(this.draggedItemIndex, 0, removedItem);
      } else if (sourceList === 'owned') {
        this.ownedImages.splice(this.draggedItemIndex, 0, removedItem);
      }
    }
  }

  private updateUI() {
    this.compensate();
    this.cdr.detectChanges();
    this.resetDragState();
  }

  onDragEnd(event: DragEvent) {
    this.resetDragState();
    const target = event.target as HTMLElement;
    target.classList.remove('dragging');
  }

  private resetDragState() {
    this.dragOverIndex = -1;
    this.draggedItemIndex = -1;
    this.dragOverList = null;
    this.currentDragList = null;
  }

  dragOverCompensateIndex: number = -1;
  onCompensateDragOver(event: DragEvent, index: number, list: 'published' | 'available') {
    event.preventDefault();
    event.stopPropagation();

    // Only show drag over effect if dragging from a different list
    // or if dragging within the same list and not already at the end
    const isFromSameList = this.currentDragList === list;
    const isAtEnd =
      (list === 'published' && this.draggedItemIndex === this.publishedImages.length - 1) ||
      (list === 'available' && this.draggedItemIndex === this.availableImages.length - 1);

    if (!isFromSameList || (isFromSameList && !isAtEnd)) {
      this.dragOverCompensateIndex = index;
      this.dragOverIndex = -1;
      this.dragOverList = list;
      event.dataTransfer!.dropEffect = 'move';
    } else {
      event.dataTransfer!.dropEffect = 'none';
    }
  }

  onCompensateDragEnter(event: DragEvent, index: number, list: 'published' | 'available') {
    event.preventDefault();
    event.stopPropagation();

    const isFromSameList = this.currentDragList === list;
    const isAtEnd =
      (list === 'published' && this.draggedItemIndex === this.publishedImages.length - 1) ||
      (list === 'available' && this.draggedItemIndex === this.availableImages.length - 1);

    if (!isFromSameList || (isFromSameList && !isAtEnd)) {
      this.dragOverCompensateIndex = index;
      this.dragOverIndex = -1;
      this.dragOverList = list;
    }
  }
}

export class EditPlaceImagesModalActions {
  confirm: (event: ButtonTriggerEvent, place: VisitorPlace, coverImg: string) => void;
  cancel?: () => void;
}

export interface IEditPlaceImagesModalData {
  actions: EditPlaceImagesModalActions;
  place: VisitorPlace;
}
